'use client';

import React, { useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import Banner from '@/features/home/modules/banner';
import Fantasy from '@/features/home/modules/fantasy';
import Rummy from '@/features/home/modules/rummy';
import Sports from '@/features/home/modules/sports';
import RecentlyPlayed from '@/features/home/modules/recently-played';
import Casino from '@/features/home/modules/casino';
import UnloginBanner from '@/features/home/modules/unlogin-banner';
import { CardType } from '@/features/home/services/home';
import type { WithdrawRequestSource } from '@/utils/track';
import {
  cardsAtom,
  setHomeDataAtom,
} from '@/features/home/store/home';
import { isLoginAtom } from '@/store';
import { useHomeInfo } from "@/features/home/services/home";
import type { HomeRes } from '@/types/api/hall/home';
import Placeholder from '@/features/home/components/placeholder';
import { track } from '@/utils/track';
import FooterContent from '@/features/home/modules/footer-content';
import { rechargeNotificationAtom, RechargeNotification } from '@/store/balance';
import useBindAccountPrompt from '@/hooks/use-bind-account-prompt';
import dayjs from 'dayjs';
import BottomContent from '@/components/layout/bottom-content';
import { utmActAtom } from '@/store/attribute-uuid';
import { useRouteActive } from '@/components/layout/route-keep-alive';
import SubBanner from '@/features/home/modules/sub-banner';

const HOME_SUB_BANNER_SOURCE: Partial<Record<CardType, WithdrawRequestSource>> = {
  [CardType.CASINO]: 'home_page_casino',
  [CardType.RUMMY]: 'home_page_rummy',
  [CardType.FANTASY]: 'home_page_fantasy',
  [CardType.SPORTS]: 'home_page_sports',
};

export default function HomePageClient() {
  const t = useTranslations('home');
  const { isActive } = useRouteActive();
  const utmAct = useAtomValue(utmActAtom);
  const isLogin = useAtomValue(isLoginAtom);
  // Jotai store hooks
  const cards = useAtomValue(cardsAtom);
  const { data: homeInfo, isLoading, isPending, refetch: refetchHomeInfo } = useHomeInfo.useQueryHook({
    utm_act: utmAct,
    queryConfig: {
      enabled: false
    }
  });

  // 使用 isPending 或者组合状态来确保初始 loading 为 true
  const isInitialLoading = isLoading || isPending;
  const setHomeInfoData = useSetAtom(setHomeDataAtom);
  const rechargeNotification = useAtomValue(rechargeNotificationAtom);
  const prevNotificationRef = useRef<RechargeNotification | null>(null);
  const { promptBindAccount } = useBindAccountPrompt();

  useEffect(() => {
    if (homeInfo) {
      setHomeInfoData(homeInfo);
    } else {
      setHomeInfoData({} as HomeRes);
    }
  }, [homeInfo, setHomeInfoData]);

  // 业务页面接口依赖
  useEffect(() => {
    refetchHomeInfo();
  }, [isLogin, refetchHomeInfo]);

  // 游客充值弱引导：仅在大厅页面触发；每日仅弹一次
  useEffect(() => {
    if (
      rechargeNotification &&
      rechargeNotification !== prevNotificationRef.current &&
      rechargeNotification.eventType === 2 && // 充值事件
      rechargeNotification.eventStatus === 1 // 成功
    ) {
      prevNotificationRef.current = rechargeNotification;

      try {
        // 以本地自然日为粒度限制弹窗频率
        const todayKey = dayjs().format('DD/MM/YYYY');
        const storageKey = 'bindAccountWeakPromptDate';
        const lastShown = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
        if (lastShown !== todayKey) {
          // 触发弱引导弹窗（其内部会根据是否需要绑定来决定是否展示）
          Promise.resolve(promptBindAccount()).finally(() => {
            try {
              if (typeof window !== 'undefined') {
                window.localStorage.setItem(storageKey, todayKey);
              }
            } catch { }
          });
        }
      } catch { }
    }
  }, [rechargeNotification, promptBindAccount]);

  // 当路由激活时，重新请求数据和跟踪
  useEffect(() => {
    if (isActive) {
      refetchHomeInfo();
      track('home_page_view', { '#url': location.href });
    }
  }, [isActive, refetchHomeInfo]);

  // 加载中显示占位
  if (isInitialLoading) {
    return <Placeholder />;
  }

  return (
    <div className="space-y-[20px]">
      {
        (homeInfo?.top_banner?.length || 0) > 0 && (
          <>
            {
              !isLogin ? (
                <UnloginBanner bannerImages={homeInfo?.top_banner || []} />
              ) : (
                <Banner bannerImages={homeInfo?.top_banner || []} from='Home' source='home_page_top_login' />
              )
            }
          </>
        )
      }
      {/* homeInfo.cards 根据cards的类型，去渲染不同的组件 */}
      {cards.map((item, index) => {
        // 组件映射表，避免重复的条件判断
        const renderCardComponent = () => {
          switch (item.type) {
            case CardType.RECENTLY_PLAYED:
              if (!item.content || item.content?.length === 0) {
                return null;
              }
              return <RecentlyPlayed recentlyPlayedGames={item.content} title={t('section.recentlyPlayed')} />;
            case CardType.CASINO:
              return <Casino />;
            case CardType.FANTASY:
              return <Fantasy />;
            case CardType.RUMMY:
              return <Rummy />;
            case CardType.SPORTS:
              return <Sports />;
            default:
              return null;
          }
        };

        return (
          <div key={index} className='gap-[20px]'>
            {renderCardComponent()}
            {/* 每个模块的banner，放在最下边 */}
            {item?.banner && <SubBanner banners={item?.banner} module={item.type} from='Home' source={HOME_SUB_BANNER_SOURCE[item.type]} />}
          </div>
        );
      })}
      {
        !isInitialLoading && (cards.length > 0) &&
        <>
          <FooterContent />
          <BottomContent />
        </>
      }
    </div>
  );
}
